import React, { ReactNode, CSSProperties } from "react";
import { useCss, k, a } from "kremling";
import { createPortal } from "react-dom";
import { m, domAnimation, AnimatePresence, LazyMotion } from "framer-motion";
import { ModalContext } from "./modal-context";

export type ModalProps = {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  show?: boolean;
  onClose?: () => void;
  allowBackdropClose?: boolean;
  disableModalExit?: boolean;
  onCloseAnimateComplete?: () => void;
  overlayOnMobile?: boolean;
};

const transition = {
  duration: 0.2,
  ease: "easeInOut",
};

export function Modal(props: ModalProps) {
  const {
    className,
    style,
    show,
    onClose,
    onCloseAnimateComplete,
    allowBackdropClose = true,
    disableModalExit,
    children,
    overlayOnMobile,
  } = props;
  const scope = useCss(css);

  return createPortal(
    <LazyMotion features={domAnimation}>
      <ModalContext.Provider value={{ onClose, disableModalExit }}>
        <AnimatePresence onExitComplete={onCloseAnimateComplete}>
          {show && (
            <div
              {...scope}
              className={a("modal-container", className)}
              style={style}
            >
              <m.div
                className="modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={transition}
                onClick={() => {
                  if (allowBackdropClose && !disableModalExit) {
                    onClose();
                  }
                }}
              />
              <m.div
                className={a("modal").m("--overlay", overlayOnMobile)}
                transition={transition}
                initial={{ translateY: 40, opacity: 0 }}
                animate={{ translateY: 0, opacity: 1 }}
                exit={{ translateY: 40, opacity: 0 }}
              >
                {children}
              </m.div>
            </div>
          )}
        </AnimatePresence>
      </ModalContext.Provider>
    </LazyMotion>,
    document.body
  );
}

const css = k`
  @include generateThemeVariables((
    modal-backdrop-bg: (
      light: rgba($raisin-500, .1),
      dark: rgba($raisin-500, .8),
    ),
  ));
  
  .modal-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    perspective: 100rem;
    z-index: 1000;
    padding: 0 1.6rem;
  }
  
  .modal-backdrop {
    position: absolute;
    background-color: var(--modal-backdrop-bg);
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    backdrop-filter: blur(5px);
  }
  
  .modal {
    position: relative;
    background-color: var(--app-background);
    width: 45rem;
    min-height: 10rem;
    border-radius: $border-radius-xxl;
    z-index: 1;
    margin: 0 auto;
    transform: rotateX(40deg);
    transform-style: preserve-3d;
    box-shadow: $shadow-lg;
    max-height: calc(100vh - 3.2rem);
    overflow: auto;
    display: flex;
    flex-direction: column;
    
    @include breakpointMax(lg) {
      //Only offer this class on sizes smaller than 'lg'
      &.--overlay {
        position: absolute;
        border-radius: 0;
        box-shadow: none;
        width: auto;
        min-height: 100%;
        height: 100%;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        overflow: hidden;
      }
    }
  }
  
  // sometimes we need to wrap a form around both the body and the header
  // to do this, we need a new flex wrapper that will auto-flow the body content
  :global .as-modal {
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
`;
